import { useState } from "react";
import { Download, FolderInput, Folder, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import type { AttachmentFolderDTO } from "@/hooks/api/useAttachmentFolders";
import {
  buildDocumentsZip,
  triggerBlobDownload,
  type DocumentsZipLayout,
} from "@/utils/downloadDocumentsZip";
import { toast } from "sonner";

type Props = {
  documents: any[];
  folders: AttachmentFolderDTO[];
  /** Used as the zip file prefix and as the single folder name in “flat” mode. */
  leadName: string;
  disabled?: boolean;
};

export default function DownloadAllDocumentsButton({
  documents,
  folders,
  leadName,
  disabled,
}: Props) {
  const [pending, setPending] = useState(false);

  const downloadable = documents.filter((d) => d?.fileUrl && String(d.fileUrl).trim());

  const run = async (layout: DocumentsZipLayout) => {
    if (pending) return;
    if (downloadable.length === 0) {
      toast.error("No files to download");
      return;
    }
    setPending(true);
    try {
      const { blob, fileName } = await buildDocumentsZip({
        documents: downloadable,
        folders,
        layout,
        leadName,
      });
      triggerBlobDownload(blob, fileName);
      toast.success("Zip downloaded");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Could not create zip";
      toast.error(msg);
    } finally {
      setPending(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1 px-2.5 text-b4 text-[var(--color-black-8)] hover:bg-black/[0.06]"
          disabled={disabled || pending || downloadable.length === 0}
          aria-label="Download all documents"
        >
          {pending ? (
            <Loader2 className="size-3.5 shrink-0 animate-spin" />
          ) : (
            <Download className="size-3.5 shrink-0" />
          )}
          <span className="hidden sm:inline">Download all</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(18rem,calc(100vw-2rem))]">
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => run("folder_tree")}
        >
          <FolderInput className="mr-2 size-4 shrink-0 text-[#3584e4]" />
          <span className="text-[13px] leading-snug">
            Same folders as CRM{" "}
            <span className="text-[var(--color-black-6)]">(nested zip)</span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => run("flat_lead_folder")}
        >
          <Folder className="mr-2 size-4 shrink-0 text-[#3584e4]" />
          <span className="text-[13px] leading-snug">
            One folder named after lead{" "}
            <span className="text-[var(--color-black-6)]">(flat)</span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
